/**
 * Storage back ends for the Troubleshooting offline copy. Both answer the same
 * small promise interface over flat records `{ id, ... }`:
 * `get(id)`, `listMeta()` (every record without its `json`), `put(record)`, `remove(id)`, `clear()`, plus
 * `maxBytes()` (what the cache may hold on that back end).
 *
 * IndexedDB is the home; localStorage is only the fallback for a browser
 * without it, with a smaller cap because its quota is shared with the app.
 * `createDefaultAdapter` picks one lazily and, when neither works (blocked
 * storage, private mode), rejects every call so the cache layer degrades to
 * "no offline copy" instead of breaking the page.
 */

const DB_NAME = 'bbvr-troubleshooting-cache';
const STORE = 'entries';
// Eviction reads only this small copy of each record, never the article bodies.
const META_STORE = 'meta';
const DB_VERSION = 2;

export const IDB_MAX_BYTES = 6 * 1024 * 1024;
export const LOCAL_STORAGE_MAX_BYTES = 1024 * 1024;
const LOCAL_PREFIX = 'bbvr_ts_cache:';

const promisify = (request) => new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed.'));
});

const finished = (tx) => new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction failed.'));
    tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted.'));
});

const openDb = (factory) => new Promise((resolve, reject) => {
    const request = factory.open(DB_NAME, DB_VERSION);
    // The cache is disposable, so an older layout is rebuilt rather than migrated.
    request.onupgradeneeded = () => {
        const db = request.result;
        [STORE, META_STORE].forEach((name) => {
            if (db.objectStoreNames.contains(name)) db.deleteObjectStore(name);
            db.createObjectStore(name, { keyPath: 'id' });
        });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB could not open.'));
    request.onblocked = () => reject(new Error('IndexedDB is blocked.'));
});

const metaOf = ({ id, user, name, cachedAt, size }) => ({ id, user, name, cachedAt, size });

/** Rejects when the database cannot be opened, so the caller can fall back. */
export const createIdbAdapter = async (factory) => {
    let db = null;
    const connect = async () => {
        if (!db) {
            const handle = await openDb(factory);
            // iOS Safari kills connections after backgrounding and upgrades close them; the next call reopens.
            handle.onclose = () => {
                if (db === handle) db = null;
            };
            handle.onversionchange = () => {
                handle.close();
                if (db === handle) db = null;
            };
            db = handle;
        }
        return db;
    };
    await connect();

    // A killed handle throws InvalidStateError only when used; reopen once and retry.
    const use = async (fn) => {
        for (let attempt = 0; ; attempt += 1) {
            const handle = await connect();
            try {
                return await fn(handle);
            } catch (error) {
                if (error?.name !== 'InvalidStateError' || attempt > 0) throw error;
                if (db === handle) db = null;
            }
        }
    };
    const read = (storeName, fn) => use((handle) => promisify(fn(handle.transaction(storeName, 'readonly').objectStore(storeName))));
    const write = (fn) => use(async (handle) => {
        const tx = handle.transaction([STORE, META_STORE], 'readwrite');
        fn(tx.objectStore(STORE), tx.objectStore(META_STORE));
        await finished(tx);
    });
    return {
        maxBytes: async () => IDB_MAX_BYTES,
        get: async (id) => (await read(STORE, (s) => s.get(id))) || null,
        listMeta: async () => (await read(META_STORE, (s) => s.getAll())) || [],
        put: (record) => write((entries, meta) => {
            entries.put(record);
            meta.put(metaOf(record));
        }),
        remove: (id) => write((entries, meta) => {
            entries.delete(id);
            meta.delete(id);
        }),
        clear: () => write((entries, meta) => {
            entries.clear();
            meta.clear();
        }),
    };
};

export const createLocalStorageAdapter = (storage) => {
    const keys = () => {
        const found = [];
        for (let i = 0; i < storage.length; i += 1) {
            const key = storage.key(i);
            if (key && key.startsWith(LOCAL_PREFIX)) found.push(key);
        }
        return found;
    };
    const read = (key) => {
        try {
            return JSON.parse(storage.getItem(key));
        } catch (e) {
            return null;
        }
    };
    return {
        maxBytes: async () => LOCAL_STORAGE_MAX_BYTES,
        get: async (id) => read(LOCAL_PREFIX + id),
        listMeta: async () => keys().map(read).filter(Boolean).map(metaOf),
        put: async (record) => storage.setItem(LOCAL_PREFIX + record.id, JSON.stringify(record)),
        remove: async (id) => storage.removeItem(LOCAL_PREFIX + id),
        clear: async () => keys().forEach((key) => storage.removeItem(key)),
    };
};

const globalIdb = () => {
    try {
        return typeof indexedDB !== 'undefined' ? indexedDB : null;
    } catch (e) {
        return null;
    }
};

const globalStorage = () => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch (e) {
        return null;
    }
};

export const createDefaultAdapter = ({ idb = globalIdb(), storage = globalStorage() } = {}) => {
    let chosen = null;
    const resolve = () => {
        if (!chosen) {
            chosen = (async () => {
                if (idb) {
                    try {
                        return await createIdbAdapter(idb);
                    } catch (e) {
                        /* fall through to localStorage */
                    }
                }
                if (storage) {
                    try {
                        const probe = `${LOCAL_PREFIX}probe`;
                        storage.setItem(probe, '1');
                        storage.removeItem(probe);
                        return createLocalStorageAdapter(storage);
                    } catch (e) {
                        /* storage blocked */
                    }
                }
                throw new Error('No storage is available for the offline copy.');
            })();
        }
        return chosen;
    };
    const through = (method) => async (...args) => (await resolve())[method](...args);
    return {
        maxBytes: through('maxBytes'),
        get: through('get'),
        listMeta: through('listMeta'),
        put: through('put'),
        remove: through('remove'),
        clear: through('clear'),
    };
};
